import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { v, num, FieldRow, KeepTogether, Section, SectionNumberingProvider, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, } from '../../../atoms/index.js';
import { expandPartySlot } from '../../../parties/index.js';
export const metadata = {
    // Document sections in fixed order (per ADR-0049). This template's
    // editor field-groups do NOT map 1:1 to numbered document sections,
    // so field.section is left as editor labels (no § shown in the form);
    // numbering + anchors come purely from these refs + the Body.
    sections: [
        { ref: 'forstahandshyresgast-er-sokande', title: 'Förstahandshyresgäst(er) (sökande)' },
        { ref: 'skal-for-uthyrning', title: 'Skäl för uthyrning' },
        { ref: 'onskad-uthyrningstid', title: 'Önskad uthyrningstid' },
        { ref: 'adress-under-uthyrningstiden', title: 'Adress under uthyrningstiden' },
        { ref: 'hyresbetalning-under-uthyrningen', title: 'Hyresbetalning under uthyrningen' },
        { ref: 'foreslagen-andrahandshyresgast', title: 'Föreslagen andrahandshyresgäst' },
        { ref: 'forutsattningar-for-andrahandsuthyrning', title: 'Förutsättningar för andrahandsuthyrning' },
        { ref: 'samtycke-till-personuppgiftsbehandling', title: 'Samtycke till personuppgiftsbehandling' },
        { ref: 'underskrift-av-kontraktsinnehavaren', title: 'Underskrift av kontraktsinnehavaren' },
        { ref: 'underskrift-av-den-foreslagna-andrahandshyresgasten', title: 'Underskrift av den föreslagna andrahandshyresgästen' },
    ],
    templateKey: 'ansokan_andrahand',
    version: 'v1',
    category: 'Andrahandsuthyrning',
    title: 'Ansökan om andrahandsuthyrning',
    shortTitle: 'Ansökan andrahandsuthyrning',
    titleSuffix: { key: 'apartment_official_number', prefix: 'lgh' },
    formNumber: 'Formulär R-08',
    preamble: 'Förstahandshyresgästen ansöker härmed om hyresvärdens samtycke till andrahandsuthyrning av nedan angiven lägenhet.',
    description: 'Förstahandshyresgästens ansökan till hyresvärden om att hyra ut lägenheten i andra hand.\n\n' +
        'Vem undertecknar: förstahandshyresgästen (en eller två) samt den föreslagna andrahandshyresgästen.\n' +
        'Leverans: printable PDF — fylls i av hyresgästerna, skrivs ut och signeras fysiskt innan den lämnas till hyresvärden för beslut. Går inte via Scrive.\n\n' +
        'Kopplad mall — skapas under det bostadshyresavtal som ansökan gäller. Bifoga gärna det påskrivna andrahandskontraktet och relevanta underlag (personbevis, anställningsavtal, antagningsbesked m.m.) som bilagor.',
    status: 'draft',
    signingMode: 'manual_delivery',
    connectedTo: ['bostadshyra'],
    // Roles do NOT line up 1:1 with the parent: the parent's hyresgäst(er)
    // (lessee 0/1) become the *applicants* here (lessor 0/1 — they become
    // the lessor of the future andrahandsavtal), and the proposed
    // andrahandshyresgäst (lessee 0) is a NEW party not present on the
    // parent. So we map explicitly and deliberately do not inherit the
    // parent's landlord (an andrahandsansökan is addressed to — not signed
    // by — the landlord) per GDPR minimisation (ADR-0055 / ADR-0009).
    inheritFromParent: {
        parties: [
            { role: 'lessor', ordinal: 0, fromRole: 'lessee', fromOrdinal: 0 },
            { role: 'lessor', ordinal: 1, fromRole: 'lessee', fromOrdinal: 1 },
        ],
        property: true,
        values: [
            { key: 'apartment_official_number', fromParentKey: 'property_official_apt_no' },
        ],
    },
    signingPlacementAnchors: 'per-party-label',
    partySlots: [
        // Modelled as the parties to the FUTURE andrahandsavtal:
        // kontraktsinnehavaren blir hyresvärd (lessor) i andrahandsavtalet,
        // den föreslagna andrahandshyresgästen blir hyresgäst (lessee).
        {
            id: 'lessor_primary',
            label: 'Förstahandshyresgäst (kontraktsinnehavare)',
            signingLabel: 'Hyresgäst 1',
            role: 'lessor',
            ordinal: 0,
            required: true,
            signs: true,
            help: 'I andrahandsavtalet blir kontraktsinnehavaren hyresvärd.',
        },
        {
            id: 'lessor_co',
            label: 'Medsökande kontraktsinnehavare',
            signingLabel: 'Eventuell hyresgäst 2',
            role: 'lessor',
            ordinal: 1,
            signs: true,
            help: 'Valfritt — lägg till om förstahandskontraktet har två hyresgäster.',
        },
        {
            id: 'lessee',
            label: 'Föreslagen andrahandshyresgäst',
            signingLabel: 'Andrahandshyresgäst',
            role: 'lessee',
            ordinal: 0,
            required: true,
            signs: true,
            help: 'Den person som ska hyra lägenheten i andra hand.',
        },
    ],
    // Party projection (ADR-0065). The förstahands applicants share one
    // section, disambiguated by "(hyresgäst 1/2)" suffixes; all are persons.
    // The lessor's "Telefon till er" sits after a property field and stays
    // hand-declared.
    partyProjection: [
        {
            slot: 'lessor_primary',
            section: 'Förstahandshyresgäst',
            idHint: 'person',
            labelSuffix: ' (hyresgäst 1)',
            fields: [
                { attr: 'legal_name', key: 'tenant_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'tenant_personnummer', help: 'PII — ej sökbart' },
            ],
        },
        {
            slot: 'lessor_co',
            section: 'Förstahandshyresgäst',
            idHint: 'person',
            labelSuffix: ' (hyresgäst 2)',
            fields: [
                { attr: 'legal_name', key: 'cotenant_name' },
                { attr: 'id_number', key: 'cotenant_personnummer', help: 'PII — ej sökbart' },
            ],
        },
        {
            slot: 'lessee',
            section: 'Föreslagen andrahandshyresgäst',
            idHint: 'person',
            fields: [
                { attr: 'legal_name', key: 'subtenant_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'subtenant_personnummer', help: 'PII — ej sökbart' },
                { attr: 'address', key: 'subtenant_address', label: 'Nuvarande adress' },
                { attr: 'email', key: 'subtenant_email' },
                { attr: 'phone', key: 'subtenant_phone' },
            ],
        },
    ],
    propertySlots: [
        {
            id: 'property',
            label: 'Lägenhet (fastighet)',
            multi: false,
            required: true,
        },
    ],
};
export const variableSchema = [
    // Förstahandshyresgäst
    {
        key: 'apartment_official_number',
        label: 'Lägenhetsnummer',
        type: 'text',
        section: 'Förstahandshyresgäst',
        help: 'T.ex. det officiella lägenhetsnumret (1101 etc.)',
        // Per-unit identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    // Förstahandshyresgäster — generated from metadata.partyProjection (ADR-0065).
    ...expandPartySlot(metadata, 'lessor_primary'),
    ...expandPartySlot(metadata, 'lessor_co'),
    {
        key: 'tenant_address',
        label: 'Adress (nuvarande)',
        type: 'textarea',
        section: 'Förstahandshyresgäst',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'address' },
    },
    {
        key: 'tenant_phone',
        label: 'Telefon till er',
        type: 'text',
        section: 'Förstahandshyresgäst',
        autofill: { kind: 'party', role: 'lessor', ordinal: 0, field: 'phone' },
    },
    // Skäl för uthyrning
    {
        key: 'sublet_reason',
        label: 'Skäl för uthyrning',
        type: 'select',
        section: 'Skäl för uthyrning',
        requiredAtFinal: true,
        options: [
            { value: 'provsambo', label: 'Provsambo', code: 'provsambo', summary: 'Subletting to try living together with a partner (trial cohabitation).' },
            { value: 'provjobba', label: 'Provjobba på annan ort', code: 'provjobba', summary: 'Subletting due to trial employment in another location.' },
            { value: 'studera', label: 'Studera på annan ort', code: 'studera', summary: 'Subletting due to studies in another location.' },
            { value: 'utlandsvistelse', label: 'Längre utlandsvistelse', code: 'utlandsvistelse', summary: 'Subletting due to an extended stay abroad.' },
            { value: 'annat', label: 'Annat skäl', code: 'annat', summary: 'Some other reason for subletting, described in free text.' },
        ],
        help: 'Bifoga relevant underlag: personbevis + sambons hyreskontrakt/köpeavtal, ' +
            'anställningsavtal + bostadsavtal på annan ort, antagningsbesked + bostadsavtal ' +
            'på studieorten, biljetter och resplan, eller annat relevant underlag.',
    },
    {
        key: 'sublet_reason_other',
        label: 'Annat skäl — beskrivning',
        type: 'textarea',
        section: 'Skäl för uthyrning',
        visibleWhen: { sublet_reason: 'annat' },
    },
    // Önskad uthyrningstid
    {
        key: 'sublet_from',
        label: 'Fr.o.m.',
        type: 'date',
        section: 'Önskad uthyrningstid',
        requiredAtFinal: true,
        // Absolute date — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    {
        key: 'sublet_to',
        label: 'T.o.m.',
        type: 'date',
        section: 'Önskad uthyrningstid',
        requiredAtFinal: true,
        // Absolute date — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    // Adress under uthyrningstiden
    {
        key: 'applicant_address_during_sublet',
        label: 'Adress under uthyrningstiden',
        type: 'textarea',
        section: 'Adress under uthyrningstiden',
        requiredAtFinal: true,
        help: 'Ej nuvarande adress — vart ska post nå er under uthyrningen?',
    },
    // Hyresbetalning under uthyrningen
    {
        key: 'payment_method',
        label: 'Hyresbetalning under uthyrningen',
        type: 'select',
        section: 'Hyresbetalning under uthyrningen',
        requiredAtFinal: true,
        options: [
            { value: 'e_faktura', label: 'E-faktura till boendeapp / Mina sidor', code: 'e_faktura', summary: 'Rent invoiced as e-invoice via the housing app / My Pages.' },
            { value: 'e_post', label: 'Hyresavi till e-postadress', code: 'e_post', summary: 'Rent invoice sent to an email address.' },
            { value: 'pappersfaktura', label: 'Pappersfaktura (ev. aviavgift tillkommer)', code: 'pappersfaktura', summary: 'Rent invoiced on paper (an invoicing fee may apply).' },
        ],
    },
    {
        key: 'payment_email',
        label: 'E-postadress för hyresavi',
        type: 'text',
        section: 'Hyresbetalning under uthyrningen',
        visibleWhen: { payment_method: 'e_post' },
    },
    {
        key: 'avi_address',
        label: 'Eventuell aviadress (endast vid pappersfaktura)',
        type: 'textarea',
        section: 'Hyresbetalning under uthyrningen',
        visibleWhen: { payment_method: 'pappersfaktura' },
    },
    // Föreslagen andrahandshyresgäst — generated from
    // metadata.partyProjection (ADR-0065).
    ...expandPartySlot(metadata, 'lessee'),
    {
        key: 'subtenant_reference',
        label: 'Referens',
        type: 'text',
        section: 'Föreslagen andrahandshyresgäst',
        help: 'Person hyresvärden kan kontakta för referens.',
    },
    {
        key: 'subtenant_household_size',
        label: 'Antal hushållsmedlemmar',
        type: 'number',
        integer: true,
        section: 'Föreslagen andrahandshyresgäst',
        min: 1,
    },
    // Förutsättningar (hyresvärdens villkor)
    {
        key: 'application_deadline_months',
        label: 'Ansökan ska lämnas in senast (månader före uthyrning)',
        type: 'number',
        section: 'Förutsättningar',
        default: 2,
        min: 1,
        help: 'Hyresvärdens handläggningstid — anger hur många månader före önskad uthyrning ansökan ska vara inne.',
    },
    // Underskrift
    {
        key: 'signing_city',
        label: 'Ort',
        type: 'text',
        section: 'Underskrift',
        requiredAtFinal: true,
        help: 'T.ex. Stockholm',
    },
    {
        key: 'signing_date',
        label: 'Datum',
        type: 'date',
        section: 'Underskrift',
        requiredAtFinal: true,
        // Absolute date — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
];
export const sampleValues = {
    apartment_official_number: '1101',
    tenant_name: 'Anna Andersson',
    tenant_personnummer: '19850412-1234',
    cotenant_name: 'Bo Andersson',
    cotenant_personnummer: '19830605-2345',
    tenant_address: 'Götgatan 35\n116 21 Stockholm',
    tenant_phone: '070-123 45 67',
    sublet_reason: 'studera',
    sublet_reason_other: '',
    sublet_from: '2026-08-01',
    sublet_to: '2027-06-30',
    applicant_address_during_sublet: 'c/o Karlsson, Storgatan 12\n753 13 Uppsala',
    payment_method: 'e_post',
    payment_email: 'anna@example.com',
    avi_address: '',
    subtenant_name: 'Erik Eriksson',
    subtenant_personnummer: '19950122-3456',
    subtenant_address: 'Lundagatan 5\n118 23 Stockholm',
    subtenant_email: 'erik@example.com',
    subtenant_phone: '073-987 65 43',
    subtenant_reference: 'Karin Karlsson, 070-555 12 34',
    subtenant_household_size: 1,
    application_deadline_months: 2,
    signing_city: 'Stockholm',
    signing_date: '2026-06-15',
};
const cond = (mode, predicate) => mode === 'blank' ? true : predicate;
function Checkbox({ checked }) {
    return (_jsx("span", { className: "mr-1 inline-block h-3 w-3 border border-stone-500 text-center align-middle leading-3", "aria-hidden": true, children: checked ? '✓' : '' }));
}
const REASON_OPTIONS = [
    { value: 'provsambo', label: 'Provsambo' },
    { value: 'provjobba', label: 'Provjobba på annan ort' },
    { value: 'studera', label: 'Studera på annan ort' },
    { value: 'utlandsvistelse', label: 'Längre utlandsvistelse' },
];
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
export function Body({ values, mode, signatories, }) {
    const reasonIsOther = values.sublet_reason === 'annat';
    return (_jsxs(SectionNumberingProvider, { sections: metadata.sections ?? [], values: values, mode: mode, children: [_jsx("p", { className: "mb-4 italic text-stone-600", children: metadata.preamble }), _jsxs(Section, { sectionRef: "forstahandshyresgast-er-sokande", title: "F\u00F6rstahandshyresg\u00E4st(er) (s\u00F6kande)", children: [_jsx(FieldRow, { label: "L\u00E4genhetsnummer", children: v(values.apartment_official_number) }), _jsx(FieldRow, { label: "Namn (hyresg\u00E4st 1)", children: v(values.tenant_name) }), _jsx(FieldRow, { label: "Personnummer (hyresg\u00E4st 1)", children: v(values.tenant_personnummer) }), cond(mode, !!values.cotenant_name || !!values.cotenant_personnummer) && (_jsxs(_Fragment, { children: [_jsx(FieldRow, { label: "Namn (hyresg\u00E4st 2)", children: v(values.cotenant_name) }), _jsx(FieldRow, { label: "Personnummer (hyresg\u00E4st 2)", children: v(values.cotenant_personnummer) })] })), _jsx(FieldRow, { label: "Adress", children: v(values.tenant_address) }), _jsx(FieldRow, { label: "Telefon till er", children: v(values.tenant_phone) })] }), _jsxs(Section, { sectionRef: "skal-for-uthyrning", title: "Sk\u00E4l f\u00F6r uthyrning", children: [_jsx("p", { className: "mt-1 mb-2 text-[10.5px] leading-relaxed", children: "Till st\u00F6d f\u00F6r ditt behov av att hyra ut i andra hand ska du bifoga relevant underlag (personbevis + sambons hyreskontrakt eller k\u00F6peavtal, anst\u00E4llningsavtal + nytt bostadsavtal p\u00E5 annan ort, antagningsbesked + bostadsavtal p\u00E5 studieorten, biljetter och resplan, eller annat relevant underlag). I samtliga fall ska ett uppr\u00E4ttat andrahandskontrakt bifogas till ans\u00F6kan." }), REASON_OPTIONS.map((opt) => (_jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: values.sublet_reason === opt.value }), opt.label] }, opt.value))), _jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: reasonIsOther }), "Annat sk\u00E4l:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: reasonIsOther ? v(values.sublet_reason_other) : ' '.repeat(40) })] })] }), _jsxs(Section, { sectionRef: "onskad-uthyrningstid", title: "\u00D6nskad uthyrningstid", children: [_jsx(FieldRow, { label: "Fr.o.m.", children: v(values.sublet_from) }), _jsx(FieldRow, { label: "T.o.m.", children: v(values.sublet_to) })] }), _jsxs(Section, { sectionRef: "adress-under-uthyrningstiden", title: "Adress under uthyrningstiden", children: [_jsx("p", { className: "text-[10.5px] italic text-stone-600", children: "Ej nuvarande adress." }), _jsx("div", { className: "mt-1 whitespace-pre-line border-b border-stone-300 px-1 font-serif text-[10.5px] leading-relaxed", children: v(values.applicant_address_during_sublet) })] }), _jsxs(Section, { sectionRef: "hyresbetalning-under-uthyrningen", title: "Hyresbetalning under uthyrningen", children: [_jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: values.payment_method === 'e_faktura' }), "E-faktura till boendeapp / Mina sidor"] }), _jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: values.payment_method === 'e_post' }), "Hyresavi till e-postadress:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: cond(mode, values.payment_method === 'e_post')
                                    ? v(values.payment_email)
                                    : ' '.repeat(30) })] }), _jsxs("p", { className: "text-[10.5px] leading-relaxed", children: [_jsx(Checkbox, { checked: values.payment_method === 'pappersfaktura' }), "Pappersfaktura (information om ev. aviavgift tillkommer)"] }), cond(mode, values.payment_method === 'pappersfaktura') && (_jsxs("div", { className: "mt-2", children: [_jsx("p", { className: "text-[10.5px] italic text-stone-600", children: "Eventuell aviadress (endast vid pappersfaktura)" }), _jsx("div", { className: "mt-1 whitespace-pre-line border-b border-stone-300 px-1 font-serif text-[10.5px] leading-relaxed", children: v(values.avi_address) })] }))] }), _jsxs(Section, { sectionRef: "foreslagen-andrahandshyresgast", title: "F\u00F6reslagen andrahandshyresg\u00E4st", children: [_jsx(FieldRow, { label: "Namn", children: v(values.subtenant_name) }), _jsx(FieldRow, { label: "Personnummer", children: v(values.subtenant_personnummer) }), _jsx(FieldRow, { label: "Nuvarande adress", children: v(values.subtenant_address) }), _jsx(FieldRow, { label: "E-post", children: v(values.subtenant_email) }), _jsx(FieldRow, { label: "Telefonnummer", children: v(values.subtenant_phone) }), _jsx(FieldRow, { label: "Referens", children: v(values.subtenant_reference) }), _jsx(FieldRow, { label: "Antal hush\u00E5llsmedlemmar", children: num(values.subtenant_household_size) })] }), _jsxs(Section, { sectionRef: "forutsattningar-for-andrahandsuthyrning", title: "F\u00F6ruts\u00E4ttningar f\u00F6r andrahandsuthyrning", children: [_jsxs("p", { className: "text-[10.5px] leading-relaxed", children: ["Ans\u00F6kan ska l\u00E4mnas in senast", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.application_deadline_months) }), ' ', "m\u00E5nad f\u00F6re \u00F6nskad uthyrning. Obs! Uthyrning utan tillst\u00E5nd kan leda till att ditt hyresavtal s\u00E4gs upp med omedelbar verkan!"] }), _jsx("p", { className: "mt-2 text-[10.5px] leading-relaxed", children: "Kontraktsinnehavaren ska ha varit skriven p\u00E5 adressen i minst 1 \u00E5r." }), _jsx("p", { className: "mt-2 text-[10.5px] leading-relaxed", children: "Du bifogar ett p\u00E5skrivet andrahandskontrakt till din ans\u00F6kan och den f\u00F6reslagna hyresg\u00E4sten \u00E4r n\u00E5gon som kan godtas av din hyresv\u00E4rd. Om du tar ut en f\u00F6r h\u00F6g hyra riskerar du upps\u00E4gning med omedelbar verkan!" }), _jsx("p", { className: "mt-2 text-[10.5px] leading-relaxed", children: "Du styrker att du har beaktansv\u00E4rda sk\u00E4l f\u00F6r andrahandsuthyrning genom att bil\u00E4gga relevant underlag till din ans\u00F6kan." }), _jsx("p", { className: "mt-3 text-[10.5px] font-semibold leading-relaxed", children: "Hyresg\u00E4sten ansvarar f\u00F6r l\u00E4genheten och hyrans betalning under uthyrningen" }), _jsx("p", { className: "mt-1 text-[10.5px] leading-relaxed", children: "Du/ni som kontraktsinnehavare ansvarar f\u00F6r l\u00E4genheten enligt b\u00E5de lag och kontraktets best\u00E4mmelser gentemot din hyresv\u00E4rd under tiden den \u00E4r uthyrd i andrahand. Det inneb\u00E4r att det \u00E4r du/ni som ansvarar f\u00F6r hyresinbetalningar, eventuella skador i l\u00E4genheten samt att inga st\u00F6rningar f\u00F6rekommer. En andrahandshyresg\u00E4st kan aldrig \u00F6verta f\u00F6rstahandshyresg\u00E4stens kontrakt. Eventuellt samtycke till andrahandsuthyrning g\u00E4ller endast under den period som f\u00F6ljer av beslutet. Vid eventuellt behov av f\u00F6rl\u00E4ngning m\u00E5ste du/ni g\u00F6ra en ny ans\u00F6kan." })] }), _jsx(Section, { sectionRef: "samtycke-till-personuppgiftsbehandling", title: "Samtycke till personuppgiftsbehandling", children: _jsx("p", { className: "text-[10.5px] leading-relaxed", children: "Andrahandshyresg\u00E4stens personuppgifter kommer att behandlas av hyresv\u00E4rden vid handl\u00E4ggningen av denna ans\u00F6kan och, vid samtycke till uthyrning, f\u00F6r att administrera andrahandsuthyrningen." }) }), _jsx("p", { className: "mt-4 mb-2 text-[10.5px] italic leading-relaxed", children: "Vi (kontraktsinnehavaren och den f\u00F6reslagna andrahandshyresg\u00E4sten) intygar p\u00E5 heder och samvete att l\u00E4mnade uppgifter \u00E4r riktiga." }), _jsx(KeepTogether, { children: _jsxs(Section, { sectionRef: "underskrift-av-kontraktsinnehavaren", title: "Underskrift av kontraktsinnehavaren", children: [_jsxs("p", { className: "mt-1 mb-3 text-[10.5px] leading-relaxed", children: [_jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.signing_city) }), ' ', "den", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.signing_date) })] }), _jsx(SigningBlock, { parties: [
                                { label: 'Hyresgäst 1', role: 'lessor', ordinal: 0 },
                                { label: 'Eventuell hyresgäst 2', role: 'lessor', ordinal: 1 },
                            ], signatories: signatories })] }) }), _jsx(KeepTogether, { children: _jsxs(Section, { sectionRef: "underskrift-av-den-foreslagna-andrahandshyresgasten", title: "Underskrift av den f\u00F6reslagna andrahandshyresg\u00E4sten", children: [_jsxs("p", { className: "mt-1 mb-3 text-[10.5px] leading-relaxed", children: [_jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.signing_city) }), ' ', "den", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.signing_date) })] }), _jsx(SigningBlock, { parties: [{ label: 'Andrahandshyresgäst', role: 'lessee', ordinal: 0 }], signatories: signatories }), _jsx("p", { className: "mt-3 text-[10.5px] italic leading-relaxed", children: "Jag samtycker h\u00E4rmed till personuppgiftsbehandling f\u00F6r administration av uthyrningen." })] }) })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map